import { Box, Text } from '@chakra-ui/react';
import * as Sentry from '@sentry/tanstackstart-react';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorComponent, type ErrorComponentProps } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { isContentNotFoundError } from '#features/error/utils/isContentNotFoundError';
import { isChunkLoadError } from '#utils/isChunkLoadError';
import { envConfig } from '#constants/envConfig';
import { canAutoReload, requestChunkReload } from '../utils/chunkReload';
import { ErrorWarning } from './ErrorWarning';
import { UpdateInProgress } from './UpdateInProgress';

export function DefaultCatchBoundary({ error }: ErrorComponentProps) {
  console.error(error);
  const queryClientErrorBoundary = useQueryErrorResetBoundary();

  // Предполагаем на первом рендере, до эффектов: иначе экран ошибки успевает мигнуть перед
  // релоадом. Только протухшая сборка и только пока не исчерпан кулдаун: повтор той же ошибки
  // сразу после перезагрузки означает, что дело не в релизе.
  //
  // Это именно предположение. Окончательный ответ даёт requestChunkReload — он ещё спрашивает
  // сервер, правда ли там другая сборка (иначе мы перезагружали бы человека, у которого просто
  // пропала связь). Ответ приходит асинхронно, поэтому решение можно отозвать.
  const [isUpdating, setIsUpdating] = useState(
    () => typeof window !== 'undefined' && isChunkLoadError(error) && canAutoReload(sessionStorage)
  );

  useEffect(() => {
    queryClientErrorBoundary.reset();
  }, [queryClientErrorBoundary]);

  useEffect(() => {
    if (isContentNotFoundError(error)) {
      return;
    }

    Sentry.captureException(error);
  }, [error]);

  useEffect(() => {
    if (!isUpdating) {
      return;
    }

    let cancelled = false;

    // Решение одно на вкладку и общее с остальными каналами, поэтому двойной прогон эффекта под
    // StrictMode безопасен: второй вызов читает тот же промис, а не заводит вторую попытку.
    void requestChunkReload({
      storage: sessionStorage,
      reload: () => window.location.reload(),
    }).then(
      // Перезагрузки не будет — сборка та же, связи нет или идёт звонок. Показываем ошибку
      // как есть: держать экран обновления, за которым ничего не последует, нельзя.
      (scheduled) => (!scheduled && !cancelled ? setIsUpdating(false) : undefined)
    );

    return () => {
      cancelled = true;
    };
  }, [isUpdating]);

  if (isUpdating) {
    return <UpdateInProgress />;
  }

  return (
    <Box w="100%" h="100%">
      <ErrorWarning />
      {/*
        Служебный разбор ошибки — только в разработке. На проде он давал второй экран ошибки
        под первым: «We couldn't load this page» с двумя кнопками, а сразу под ним «Something
        went wrong! Show Error» с трассой. Человеку это ничего не объясняет и выглядит так,
        будто сломалось дважды; в Sentry ошибка уходит и без этого блока.
      */}
      {envConfig.IN_DEV ? (
        <Text maxW="800px">
          <ErrorComponent error={error} />
        </Text>
      ) : null}
    </Box>
  );
}
